// frontend/src/components/ChatRoom.jsx
import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import EmojiPicker from 'emoji-picker-react';
import {
  joinRoom,
  leaveRoom,
  sendMessage,
  getRoomUsers,
  onRoomUsers,
  onMessage,
  onUserJoined,
  onUserLeft,
  toggleLike,
  onLikesUpdate
} from '../services/socket';
import { canonicalZodiacRoomId, mergeRoomSnapshot } from '../utils/zodiacRoomIdentity';
import { REST_API_ORIGIN } from '../platform/endpoints';
import { localizedValue } from '../localization/presentationLanguage';

const chatText = (es, en) => localizedValue({ es, en });
// (optional) if you later wire a dedicated friend-request socket/API helper:
// import { sendFriendRequest } from '../services/friends';

const API_ORIGIN = REST_API_ORIGIN;

const SIGN_EMOJI = {
  aries: '♈', tauro: '♉', geminis: '♊', 'géminis': '♊', cancer: '♋', 'cáncer': '♋',
  leo: '♌', virgo: '♍', libra: '♎', escorpio: '♏', sagitario: '♐',
  capricornio: '♑', acuario: '♒', piscis: '♓',
};

const MAX_MESSAGE_CHARS = 240; // límite de caracteres por mensaje

function toCanonical(s) {
  if (!s) return '';
  return s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

// Canonicalize a name: lower, trim, strip accents
function getCanonicalName(name) {
  if (!name) return '';
  return name
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}
export function genderEmoji(g) {
  if (g === undefined || g === null) return '';
  const s = String(g).trim();
  if (!s || s === 'null' || s === 'undefined') return '';

  // If already an emoji, return as-is
  if (s.includes('♂') || s.includes('♀') || s.includes('⚧')) return s;

  // Numeric / boolean-like
  if (typeof g === 'number') {
    if (g === 1) return '♂';
    if (g === 0 || g === 2) return '♀';
  }
  if (s === '1') return '♂';
  if (s === '0' || s === '2') return '♀';
  if (s.toLowerCase() === 'true') return '♂';
  if (s.toLowerCase() === 'false') return '♀';

  // Text labels (normalized)
  const norm = s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const masc = new Set(['hombre','masculino','m','male','man','varon','masc','h']);
  const fem  = new Set(['mujer','femenino','f','female','woman','fem']);
  const nb   = new Set(['no binario','nobinario','nonbinary','no-binario','nb','otro','other','x','trans','intersex','todes','todos','all','*','non-binary']);

  if (masc.has(norm)) return '♂';
  if (fem.has(norm))  return '♀';
  if (nb.has(norm))   return '⚧';

  return '';
}


function resolveGender(u = {}) {
  try {
    const stored = JSON.parse(localStorage.getItem('user')) || {};
    const storedId = stored._id || stored.id || stored.userId || stored.uid || null;
    const uid = u.userId || u._id || u.id || u.uid || null;
    const isMe = storedId && uid && String(storedId) === String(uid);

    // Preferimos campos explícitos; aceptamos si el server mandó el símbolo por error (genderEmoji).
    // Como último recurso, si es "yo", leemos de localStorage.
    return (
      (u && u.gender) ??
      (u && u.snapshotUser && u.snapshotUser.gender) ??
      (u && u.genderEmoji) ??
      (isMe ? stored.gender : null)
    );
  } catch {
    return u?.gender ?? u?.snapshotUser?.gender ?? u?.genderEmoji ?? null;
  }
}
function toPhotoURL(p) {
  if (!p) return '/default-profile.png';
  if (/^https?:\/\//i.test(p)) return p;
  let clean = String(p)
    .replace(/^https?:\/\/[^/]+/i, '')
    .replace(/^\//, '');
  clean = clean.replace(/^uploads\//, '');
  if (!clean.startsWith('profile_photos/')) clean = `profile_photos/${clean}`;
  return `${API_ORIGIN}/${encodeURI(clean)}`;
}

function getUserId(u = {}) {
  const candidate = u._id || u.userId || u.id || u.uid || null;
  // Public-room profile navigation accepts only canonical Mongo user ids.
  // Socket/tab/room ids are never valid profile identifiers.
  return /^[a-f\d]{24}$/i.test(String(candidate || '')) ? String(candidate) : null;
}

function normalizeUser(u = {}) {
  const id = getUserId(u);
  return {
    id,
    fullName: u.fullName || u.name || u.username || 'Anónimo',
    zodiacSign: u.zodiacSign || u.sign || '',
    gender: u.gender ?? null,
    photoPath: u.photoPath || u.photo || '',
  };
}

// Prefer gender from session snapshot (set by socket service) when localStorage lacks it
function getSessionProfileGender() {
  try {
    const raw = sessionStorage.getItem('profileSnapshot');
    if (!raw) return null;
    const p = JSON.parse(raw) || {};
    const g =
      (Object.prototype.hasOwnProperty.call(p, 'gender') ? p.gender : undefined) ??
      (Object.prototype.hasOwnProperty.call(p, 'sexo') ? p.sexo : undefined) ??
      (Object.prototype.hasOwnProperty.call(p, 'genderIdentity') ? p.genderIdentity : undefined) ??
      null;
    return g ?? null;
  } catch {
    return null;
  }
}

// --- Identity helpers (prefer localStorage for "me")
function getStoredIdentity() {
  try {
    const uUser = JSON.parse(localStorage.getItem('user') || '{}');
    const uMe   = JSON.parse(localStorage.getItem('me')   || '{}');

    // Merge, preferring explicit values from 'user', otherwise from 'me'
    const merged = {
      ...uMe,
      ...uUser,
    };

    const pick = (obj, keys) => {
      for (const k of keys) {
        if (Object.prototype.hasOwnProperty.call(obj, k) && obj[k] != null) return obj[k];
      }
      return null;
    };

    const id =
      pick(merged, ['_id','id','userId','uid']) ||
      pick(uUser, ['_id','id','userId','uid']) ||
      pick(uMe,   ['_id','id','userId','uid']) ||
      null;

    // Robustly pick gender from multiple possible keys
    const rawGender =
      pick(merged, ['gender','sexo','genderIdentity']) ??
      pick(uUser,  ['gender','sexo','genderIdentity']) ??
      pick(uMe,    ['gender','sexo','genderIdentity']) ??
      null;

    const fullName =
      pick(merged, ['fullName','name','username']) ??
      pick(uUser,  ['fullName','name','username']) ??
      pick(uMe,    ['fullName','name','username']) ??
      null;

    return { id, gender: rawGender, fullName };
  } catch {
    return { id: null, gender: null, fullName: null };
  }
}
function isSameId(a, b) {
  if (!a || !b) return false;
  return String(a) === String(b);
}
function isMeUserLike(user) {
  const me = getStoredIdentity();
  const uid = user?._id || user?.id || user?.userId || user?.uid || null;
  if (uid && me.id) return isSameId(uid, me.id);
  // fallback by canonicalized name if no ids (rare)
  const uname = getCanonicalName(user?.fullName || user?.name || user?.username || '');
  const mname = getCanonicalName(me.fullName || '');
  return !!uname && !!mname && uname === mname;
}

// Helper to reliably extract message author with consistent id
function getMsgAuthor(msg, displayName) {
  const su = msg?.snapshotUser || {};
  const id =
    su._id || su.id || msg?.userId || msg?.senderId || msg?.authorId || null;
  return {
    id,
    fullName: displayName || su.fullName || msg?.user || 'Anónimo',
    zodiacSign: su.zodiacSign || '',
    gender: su.gender ?? null,
    
    photo: su.photo || '',
  };
}

/**
 * ChatRoom
 * - No muestra “Sala …” para evitar redundancia con RoomScreen.
 * - Si querés forzar un header acá, pasá showHeader={true}.
 */
const ChatRoom = ({ room, showHeader = false }) => {
  // Marca esta pantalla como "chat-page" para desactivar gradiente/rueda global (ver index.css)
  useEffect(() => {
    document.body.classList.add('chat-page');
    return () => document.body.classList.remove('chat-page');
  }, []);
  useEffect(() => {
    const prev = window.history.scrollRestoration;
    try { window.history.scrollRestoration = 'manual'; } catch {}
    window.scrollTo(0, 0);
    return () => {
      try { window.history.scrollRestoration = prev || 'auto'; } catch {}
    };
  }, []);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [usersRaw, setUsersRaw] = useState([]);
  const [likesState, setLikesState] = useState({});
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showUsersPanel, setShowUsersPanel] = useState(false);

  // --- Virtualization state for "Usuarios en sala" modal ---
  const VIRT_ITEM_H = 60;             // fixed row height (px)
  const VIRT_OVERSCAN = 6;            // render ahead/behind rows
  const usersListRef = useRef(null);
  const [virtState, setVirtState] = useState({ scrollTop: 0, height: 0 });

  const emojiPickerRef = useRef(null);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);
  const chatBoxRef = useRef(null);
  const atBottomRef = useRef(true);
  const userHasInteractedRef = useRef(false);
  const userIsAtBottomRef = useRef(true);
  const isTouchingRef = useRef(false);


  // 👉 Autoscroll solo si el usuario ya está al fondo (evita tironeos)
  useEffect(() => {
    const el = chatBoxRef.current;
    if (!el) return;
    if (userIsAtBottomRef.current) {
      // snap inmediato al final si estaba abajo
      el.scrollTop = el.scrollHeight;
    }
  }, [messages.length]);

  useEffect(() => {
    const el = chatBoxRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
    const isAtBottom = distanceFromBottom <= 2;
    atBottomRef.current = isAtBottom;
    userIsAtBottomRef.current = isAtBottom;
  }, []);

  const getBottomDistance = () => {
    const el = chatBoxRef.current;
    if (!el) return Infinity;
    return el.scrollHeight - (el.scrollTop + el.clientHeight);
  };

  const snapToBottomIfNear = useCallback((threshold = 32) => {
    const el = chatBoxRef.current;
    if (!el) return;
    const distance = getBottomDistance();
    if (distance <= threshold) {
      el.scrollTop = el.scrollHeight - el.clientHeight;
      atBottomRef.current = true;
      userIsAtBottomRef.current = true;
    }
  }, []);

  const scrollToBottomSmooth = useCallback(() => {
    const el = chatBoxRef.current;
    if (!el) return;
    try {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    } catch {
      // fallback para navegadores sin smooth
      el.scrollTop = el.scrollHeight;
    }
  }, []);

  const isNearBottom = useCallback((threshold = 56) => {
    const el = chatBoxRef.current;
    if (!el) return true;
    const distance = el.scrollHeight - (el.scrollTop + el.clientHeight);
    return distance <= threshold;
  }, []);

  // --- Stable refs to avoid re-binding socket listeners / re-joining rooms on re-renders ---
  const roomRef = useRef(room);
  const joinedRoomRef = useRef(null);
  const isNearBottomRef = useRef(isNearBottom);
  const scrollToBottomSmoothRef = useRef(scrollToBottomSmooth);

  useEffect(() => { roomRef.current = room; }, [room]);
  useEffect(() => { isNearBottomRef.current = isNearBottom; }, [isNearBottom]);
  useEffect(() => { scrollToBottomSmoothRef.current = scrollToBottomSmooth; }, [scrollToBottomSmooth]);

  useEffect(() => {
    const root = chatBoxRef.current;
    const target = chatEndRef.current;
    if (!root || !target || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        const isIntersecting = !!entry?.isIntersecting;
        atBottomRef.current = isIntersecting;
        userIsAtBottomRef.current = isIntersecting;
      },
      { root, threshold: 0.999 }
    );
    io.observe(target);
    return () => io.disconnect();
  }, []);

  const handleChatScroll = () => {
    const el = chatBoxRef.current;
    if (!el) return;
    userHasInteractedRef.current = true;
    const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
    const isAtBottom = distanceFromBottom <= 2;
    atBottomRef.current = isAtBottom;
    userIsAtBottomRef.current = isAtBottom;
  };

  const onTouchStartBox = useCallback(() => {
    isTouchingRef.current = true;
  }, []);
  
  const onTouchEndBox = useCallback(() => {
    isTouchingRef.current = false;
    setTimeout(() => snapToBottomIfNear(36), 60);
  }, [snapToBottomIfNear]);

  useEffect(() => {
    const box = chatBoxRef.current;
    if (!box) return;
    box.addEventListener('touchstart', onTouchStartBox, { passive: true });
    box.addEventListener('touchend', onTouchEndBox, { passive: true });
    return () => {
      box.removeEventListener('touchstart', onTouchStartBox);
      box.removeEventListener('touchend', onTouchEndBox);
    };
  }, [onTouchStartBox, onTouchEndBox]);

  // --- Keep composer floating above soft keyboard (Android/iOS) ---
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const updateKbOffset = () => {
      try {
        // How much of the viewport is occluded by the keyboard / system UI
        const occluded = Math.max(0, (window.innerHeight - vv.height - vv.offsetTop));
        const clamped = Math.max(0, Math.min(400, Math.round(occluded)));

        // Base gap so the composer floats even when keyboard is closed.
        // Give Android a slightly larger base gap because safe-area is usually 0.
        const ua = (navigator.userAgent || '').toLowerCase();
        const isAndroid = ua.includes('android');
        const baseGap = isAndroid ? 24 : 16; // px

        document.documentElement.style.setProperty('--kb-safe-offset', `${clamped}px`);
        document.documentElement.style.setProperty('--kb-base-gap', `${baseGap}px`);
      } catch {}
    };

    // Initial measure + listeners
    updateKbOffset();
    try {
      const ua = (navigator.userAgent || '').toLowerCase();
      const isAndroid = ua.includes('android');
      document.documentElement.style.setProperty('--kb-base-gap', `${isAndroid ? 24 : 16}px`);
    } catch {}
    vv.addEventListener('resize', updateKbOffset);
    vv.addEventListener('scroll', updateKbOffset);
    window.addEventListener('orientationchange', updateKbOffset);

    return () => {
      try {
        vv.removeEventListener('resize', updateKbOffset);
        vv.removeEventListener('scroll', updateKbOffset);
        window.removeEventListener('orientationchange', updateKbOffset);
        document.documentElement.style.removeProperty('--kb-safe-offset');
      } catch {}
    };
  }, []);

  const navigate = useNavigate();

  // Read localStorage user only once (avoid repeated JSON.parse on every render)
  const storedUserMemo = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}') || {};
    } catch {
      return {};
    }
  }, []);

  // Memoize "me" identity/session data used across message + users rendering
  const meIdentityMemo = useMemo(() => getStoredIdentity(), []);
  const sessionGenderMemo = useMemo(() => getSessionProfileGender(), []);

  // Prevent duplicated socket listeners in React StrictMode / remounts.
  const socketBoundRef = useRef(false);
  const roomUsersRefreshTimerRef = useRef(null);
  const latestRoomSnapshotAtRef = useRef(0);

  // Cerrar panel de usuarios con Escape
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setShowUsersPanel(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Wire scroll/resize only when the users panel is visible
  useEffect(() => {
    if (!showUsersPanel) return;
    const el = usersListRef.current;
    if (!el) return;

    const onScroll = () => {
      setVirtState(v => ({ ...v, scrollTop: el.scrollTop || 0 }));
    };
    const measure = () => {
      setVirtState(v => ({ ...v, height: el.clientHeight || 0 }));
    };

    // initial measure
    measure();
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', measure);

    // slight delay to catch first layout
    const t = setTimeout(measure, 0);

    return () => {
      clearTimeout(t);
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', measure);
    };
  }, [showUsersPanel]);

  const goToUserProfile = useCallback((user) => {
    const nu = normalizeUser(user);
    let id = nu.id;
    if (!id && Array.isArray(usersRaw) && usersRaw.length) {
      const match = usersRaw.find(u => {
        const a = (u.fullName || u.name || u.username || '').toLowerCase();
        const b = (nu.fullName || '').toLowerCase();
        return a && b && a === b;
      });
      id = match ? (match._id || match.id || match.userId || match.uid || null) : null;
    }
    if (!id) return;
    navigate(`/profile/${encodeURIComponent(id)}`, { state: { fromRoom: room } });
  }, [navigate, room, usersRaw]);

  const scheduleRoomUsersRefresh = useCallback((forcedRoom) => {
    const targetRoom = forcedRoom || roomRef.current;
    if (!targetRoom) return;
    try {
      if (roomUsersRefreshTimerRef.current) clearTimeout(roomUsersRefreshTimerRef.current);
    } catch {}
    roomUsersRefreshTimerRef.current = setTimeout(() => {
      roomUsersRefreshTimerRef.current = null;
      try { getRoomUsers(targetRoom); } catch {}
    }, 80);
  }, []);


  // Bind socket listeners ONCE (avoid duplicated handlers causing repeated identify/joinRoom spam)
  useEffect(() => {
    if (socketBoundRef.current) return;
    socketBoundRef.current = true;
    const unsubscribeMessage = onMessage((msg) => {
      const displayName = msg?.snapshotUser?.fullName || msg?.fullName || msg?.user || 'Anónimo';
      const photo = toPhotoURL(msg?.snapshotUser?.photo || msg?.photo || '');
      const sign  = msg?.snapshotUser?.zodiacSign || msg?.zodiacSign || '';
      const g     = msg?.snapshotUser?.gender || msg?.gender || null;

      const box = chatBoxRef.current;
      const prevScrollHeight = box ? box.scrollHeight : 0;
      const prevScrollTop    = box ? box.scrollTop : 0;
      const wasAtBottom      = !!userIsAtBottomRef.current;
      const meStored = storedUserMemo;
      const meNameLC = (meStored.fullName || '').toLowerCase();
      const isMineIncoming = (displayName || '').toLowerCase() === meNameLC;

      const normalized = {
        id: msg?.id,
        user: displayName,
        text: msg?.text ?? msg?.message ?? '',
        snapshotUser: {
          _id:
            (msg?.snapshotUser && (msg.snapshotUser._id || msg.snapshotUser.id)) ||
            msg?.userId ||
            msg?.senderId ||
            msg?.authorId ||
            null,
          id:
            (msg?.snapshotUser && (msg.snapshotUser.id || msg.snapshotUser._id)) ||
            msg?.userId ||
            msg?.senderId ||
            msg?.authorId ||
            null,
          fullName: displayName,
          photo,
          zodiacSign: sign,
          gender: g,
          genderEmoji: (() => {
            const maybeMeId =
              (msg?.snapshotUser && (msg.snapshotUser._id || msg.snapshotUser.id)) ||
              msg?.userId || msg?.senderId || msg?.authorId || null;
            const me = meIdentityMemo;
            const sessionGender = sessionGenderMemo;
            const src =
              (maybeMeId && me.id && String(maybeMeId) === String(me.id))
                ? (me.gender ?? sessionGender ?? msg?.snapshotUser?.gender ?? msg?.gender ?? null)
                : (msg?.snapshotUser?.genderEmoji ?? msg?.genderEmoji ?? msg?.snapshotUser?.gender ?? msg?.gender ?? null);
            return genderEmoji(src);
          })(),
        },
        timestamp: msg?.timestamp || Date.now(),
        system: !!msg?.system,
      };
      setMessages(prev => [...prev, normalized]);

      if (typeof msg?.likesCount === 'number' && msg?.id) {
        setLikesState(prev => ({ ...prev, [msg.id]: msg.likesCount }));
      }

      requestAnimationFrame(() => {
        const boxAfter = chatBoxRef.current;
        if (!boxAfter) return;

        // If user was at bottom, message is mine, or near bottom -> autoscroll
        const near = isNearBottomRef.current ? isNearBottomRef.current(64) : true;
        if (wasAtBottom || isMineIncoming || near) {
          const fn = scrollToBottomSmoothRef.current;
          if (typeof fn === 'function') fn();
          return;
        }

        // Preserve reading position if user is up
        const delta = boxAfter.scrollHeight - prevScrollHeight;
        if (delta !== 0) {
          boxAfter.scrollTop = prevScrollTop + Math.max(0, delta);
        }
      });
    });

    const unsubscribeRoomUsers = onRoomUsers((payload) => {
      const activeRoom = canonicalZodiacRoomId(roomRef.current);
      const payloadRoom = canonicalZodiacRoomId(payload?.room);
      if (!activeRoom || payloadRoom !== activeRoom) return;
      setUsersRaw((previous) => {
        const merged = mergeRoomSnapshot(previous, payload, latestRoomSnapshotAtRef.current);
        latestRoomSnapshotAtRef.current = merged.snapshotAt;
        return merged.users;
      });
    });

    const unsubscribeLikesUpdate = onLikesUpdate(({ messageId, likesCount }) => {
      setLikesState(prev => ({ ...prev, [messageId]: likesCount }));
    });

    const unsubscribeUserJoined = onUserJoined(() => {
      scheduleRoomUsersRefresh(roomRef.current);
    });

    const unsubscribeUserLeft = onUserLeft(() => {
      scheduleRoomUsersRefresh(roomRef.current);
    });

    const handleClickOutside = (e) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      socketBoundRef.current = false;
      try {
        if (roomUsersRefreshTimerRef.current) clearTimeout(roomUsersRefreshTimerRef.current);
      } catch {}
      roomUsersRefreshTimerRef.current = null;
      unsubscribeMessage?.();
      unsubscribeRoomUsers?.();
      unsubscribeLikesUpdate?.();
      unsubscribeUserJoined?.();
      unsubscribeUserLeft?.();
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [storedUserMemo, meIdentityMemo, sessionGenderMemo, scheduleRoomUsersRefresh]);

  // Join/leave room ONLY when room changes (prevents repeated identify/joinRoom loops)
  useEffect(() => {
    const storedUser = storedUserMemo;
    const fullName   = storedUser.fullName || storedUser.name || storedUser.username || 'Anónimo';
    const zodiacSign = storedUser.zodiacSign || storedUser.sign || '';
    const photoPath  = storedUser.photoPath || storedUser.photo || '';
    const meIdentity = meIdentityMemo;
    const sessionGender = sessionGenderMemo;
    const gender = (Object.prototype.hasOwnProperty.call(storedUser, 'gender') ? storedUser.gender : undefined)
                ?? (Object.prototype.hasOwnProperty.call(storedUser, 'sexo') ? storedUser.sexo : undefined)
                ?? (typeof meIdentity.gender !== 'undefined' ? meIdentity.gender : undefined)
                ?? sessionGender
                ?? null;
    const userId     = storedUser._id || storedUser.id || storedUser.userId || storedUser.uid || null;

    // If we were previously joined to another room, leave it first
    if (joinedRoomRef.current && joinedRoomRef.current !== room) {
      try { leaveRoom(joinedRoomRef.current); } catch {}
      joinedRoomRef.current = null;
    }

    // Idempotent join
    if (room && joinedRoomRef.current !== room) {
      joinRoom({ room, fullName, zodiacSign, photoPath, gender, userId });
      joinedRoomRef.current = room;
      getRoomUsers(room);
    }

    return () => {
      // On unmount or when room changes, leave the current room
      if (joinedRoomRef.current) {
        try { leaveRoom(joinedRoomRef.current); } catch {}
        joinedRoomRef.current = null;
      }
    };
  }, [room, storedUserMemo, meIdentityMemo, sessionGenderMemo]);

  const users = useMemo(() => {
    return usersRaw.map(u => {
      const signRaw = toCanonical(u.zodiacSign || '');
      const me = meIdentityMemo;
      const isMe = isMeUserLike(u);
      const sessionGender = sessionGenderMemo;
      const genderToShow = isMe
        ? (u.genderEmoji ?? u.gender ?? u.sexo ?? sessionGender ?? me.gender ?? resolveGender(u))
        : (u.genderEmoji ?? u.gender ?? u.sexo ?? resolveGender(u));
      return {
        ...u,
        id: u._id || u.userId || u.id || null,
        fullName: u.fullName || u.name || u.username || 'Anónimo',
        emoji: SIGN_EMOJI[signRaw] || '',
        genderEmoji: genderEmoji(genderToShow),
        photoPath: toPhotoURL(u.photoPath || u.photo),
      };
    });
  }, [usersRaw, meIdentityMemo, sessionGenderMemo]);

  // --- Virtual window calculation for users list ---
  const totalUsers = Array.isArray(users) ? users.length : 0;
  const startIndexVirt = Math.max(
    0,
    Math.floor((virtState.scrollTop || 0) / VIRT_ITEM_H) - VIRT_OVERSCAN
  );
  const visibleCountVirt = Math.ceil(((virtState.height || 0) / VIRT_ITEM_H)) + VIRT_OVERSCAN * 2;
  const endIndexVirt = Math.min(totalUsers, startIndexVirt + visibleCountVirt);
  const offsetYVirt = startIndexVirt * VIRT_ITEM_H;
  const visibleUsers = (Array.isArray(users) ? users : []).slice(startIndexVirt, endIndexVirt);

  const handleSendMessage = (e) => {
    e.preventDefault();
    const trimmed = (message || '').trim();
    if (!trimmed) return;
    const limited = trimmed.slice(0, MAX_MESSAGE_CHARS);

    const storedUser = storedUserMemo;
    const meIdentity = meIdentityMemo;
    const sessionGender = sessionGenderMemo;
    const effGender = (Object.prototype.hasOwnProperty.call(storedUser, 'gender') ? storedUser.gender : undefined)
                   ?? (Object.prototype.hasOwnProperty.call(storedUser, 'sexo') ? storedUser.sexo : undefined)
                   ?? (typeof meIdentity.gender !== 'undefined' ? meIdentity.gender : undefined)
                   ?? sessionGender
                   ?? null;
    sendMessage({
      room,
      text: limited,
      fullName: storedUser.fullName || 'Anónimo',
      photoPath: storedUser.photoPath || '',
      zodiacSign: storedUser.zodiacSign || '',
      gender: effGender,
    });

    setMessage('');
    requestAnimationFrame(() => {
      scrollToBottomSmooth();
    });
  };

  const handleBack = () => navigate(-1);

  const addEmoji = (emojiData) => {
    setMessage(prev => prev + (emojiData?.emoji ?? ''));
    inputRef.current?.focus();
    setTimeout(() => setShowEmojiPicker(false), 0);
  };

  const storedUser = storedUserMemo;

  const handleToggleLike = (msgId) => {
    if (!msgId) return;
    const userKey = storedUser._id || storedUser.userId || storedUser.fullName || 'Anon';
    toggleLike({ room, messageId: msgId, userKey });
  };

  return (
    <div id="chatRoot">
      <div style={styles.container}>
      {showHeader && (
        <div style={styles.headerBar}>
          <button onClick={handleBack} style={styles.backButton}>{chatText('← Volver', '← Back')}</button>
        </div>
      )}

      {/* Botón flotante con conteo de usuarios */}
      <button
        type="button"
        onClick={() => setShowUsersPanel(true)}
        style={styles.usersFab}
        aria-haspopup="dialog"
        aria-expanded={showUsersPanel ? 'true' : 'false'}
        aria-controls="usersPanel"
        title={chatText('Usuarios online', 'Users online')}
      >
        👥 {Array.isArray(users) ? users.length : 0} online
      </button>

      {/* Chat */}
      {/* Modal de usuarios en sala */}
      {showUsersPanel && (
        <div
          id="usersPanelOverlay"
          style={styles.usersModalOverlay}
          role="presentation"
          onClick={(e) => {
            // cerrar al click fuera del panel
            if (e.target.id === 'usersPanelOverlay') setShowUsersPanel(false);
          }}
        >
          <div
            id="usersPanel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="usersPanelTitle"
            style={styles.usersModal}
          >
            <div style={styles.usersModalHeader}>
              <h3 id="usersPanelTitle" style={styles.usersModalTitle}>{chatText('Usuarios en sala', 'Users in room')}</h3>
              <button
                type="button"
                onClick={() => setShowUsersPanel(false)}
                aria-label={chatText('Cerrar', 'Close')}
                title={chatText('Cerrar', 'Close')}
                style={styles.usersModalClose}
              >
                ✕
              </button>
            </div>

            <div
              style={{ ...styles.usersModalList, position: 'relative' }}
              role="list"
              ref={usersListRef}
            >
              {totalUsers === 0 ? (
                <div style={{ color: '#CBBE8A', fontWeight: 700, padding: '8px 4px' }}>{chatText('No hay usuarios', 'No users')}</div>
              ) : (
                <>
                  {/* Spacer to give full scroll range */}
                  <div style={{ height: totalUsers * VIRT_ITEM_H, pointerEvents: 'none' }} />
                  {/* Windowed items layer */}
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 12,
                      right: 12,
                      transform: `translateY(${offsetYVirt}px)`,
                      willChange: 'transform',
                    }}
                  >
                    {visibleUsers.map((u) => (
                      <div
                        key={u._id || u.id || u.userId || u.fullName}
                        style={{ ...styles.usersModalItem, height: VIRT_ITEM_H, boxSizing: 'border-box' }}
                        role="listitem"
                        onClick={() => goToUserProfile(u)}
                        onKeyDown={(ev) => { if (ev.key === 'Enter') goToUserProfile(u); }}
                        tabIndex={0}
                        title={`Ver perfil de ${u.fullName}`}
                      >
                        <img
                          src={u.photoPath}
                          alt={`${u.fullName} foto`}
                          style={styles.usersModalAvatar}
                          loading="lazy"
                          decoding="async"
                          onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/default-profile.png'; }}
                        />
                        <span style={styles.usersModalName}>{u.fullName}</span>
                        {!!u.emoji && <span style={styles.usersModalSign} aria-hidden="true">{u.emoji}</span>}
                        {!!u.genderEmoji && <span style={styles.usersModalGender} aria-hidden="true">{u.genderEmoji}</span>}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      <div
        style={styles.chatBox}
        ref={chatBoxRef}
        onScroll={handleChatScroll}
        onTouchStart={onTouchStartBox}
        onTouchEnd={onTouchEndBox}
        id="chatBox"
      >
        <div aria-hidden="true" style={styles.chatWheelLayer}>
          <div style={styles.chatWheel} />
        </div>

        {messages.map((msg, i) => {
          const displayName = msg?.snapshotUser?.fullName || msg?.user || 'Anónimo';
          const signKey = toCanonical(msg?.snapshotUser?.zodiacSign || '');
          const signEm = SIGN_EMOJI[signKey] || '';
          const me = meIdentityMemo;
          // Use canonical name match for "me"
          const maybeMeId =
            (msg?.snapshotUser && (msg.snapshotUser._id || msg.snapshotUser.id)) ||
            msg?.userId || msg?.senderId || msg?.authorId || null;
          const byIdIsMe = maybeMeId && me.id && isSameId(maybeMeId, me.id);
          const byNameIsMe = !maybeMeId &&
            (getCanonicalName(displayName) === getCanonicalName(me.fullName));
          // Always prefer localStorage gender for "me"
          const gEmoji = (() => {
            const sessionGender = sessionGenderMemo;
            const prefer = (byIdIsMe || byNameIsMe)
              ? (msg?.snapshotUser?.genderEmoji ?? msg?.genderEmoji ?? msg?.snapshotUser?.gender ?? msg?.gender ?? msg?.snapshotUser?.sexo ?? msg?.sexo ?? sessionGender ?? me.gender ?? null)
              : (msg?.snapshotUser?.genderEmoji ?? msg?.genderEmoji ?? msg?.snapshotUser?.gender ?? msg?.gender ?? msg?.snapshotUser?.sexo ?? msg?.sexo ?? null);
            return genderEmoji(prefer);
          })();
          const likesCount = (msg?.id && likesState[msg.id] !== undefined) ? likesState[msg.id] : 0;

          if (msg.system) {
            return (
              <div key={`sys-${i}`} style={styles.systemMsg}>
                {msg.text}
              </div>
            );
          }

          const isMine = byIdIsMe || byNameIsMe;
          const ProfileAuthor = getMsgAuthor(msg, displayName);

          const timeText = (() => {
            const d = new Date(msg.timestamp || Date.now());
            const hh = String(d.getHours()).padStart(2, '0');
            const mm = String(d.getMinutes()).padStart(2, '0');
            return `${hh}:${mm}`;
          })();

          return (
            <div key={`${msg.id || 'msg'}-${i}`} style={isMine ? styles.messageRowInlineMine : styles.messageRowInlineOther}>
              <div style={styles.inlineTopRow}>
                <img
                  src={toPhotoURL(ProfileAuthor.photo || msg?.snapshotUser?.photo || msg?.photo || '')}
                  alt={`${displayName} foto`}
                  crossOrigin="anonymous"
                  onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/default-profile.png'; }}
                  style={isMine ? { ...styles.inlineAvatar, ...styles.inlineAvatarMine } : { ...styles.inlineAvatar, ...styles.inlineAvatarOther }}
                  onClick={() => goToUserProfile(ProfileAuthor)}
                  role="button"
                  title={`Ver perfil de ${displayName}`}
                />
                <span
                  style={isMine ? styles.userNameMine : styles.userNameOther}
                  onClick={() => goToUserProfile(ProfileAuthor)}
                  onKeyDown={(ev) => { if (ev.key === 'Enter') goToUserProfile(ProfileAuthor); }}
                  role="button"
                  tabIndex={0}
                  title={`Ver perfil de ${displayName}`}
                  aria-label={`Ver perfil de ${displayName}`}
                >
                  {displayName}
                </span>
                {signEm && <span style={styles.inlineEmoji} aria-hidden="true">{signEm}</span>}
                {!!gEmoji && <span style={styles.inlineEmoji} aria-hidden="true">{gEmoji}</span>}
                <button
  type="button"
  onClick={() => goToUserProfile(ProfileAuthor)}
  style={styles.inlineAddBtnTiny}
  aria-label={chatText(`Ver Perfil de ${displayName}`, `View ${displayName}'s profile`)}
  title={chatText('Ver Perfil', 'View Profile')}
>
  {chatText('Ver Perfil', 'View Profile')}
</button>
              </div>

              <div style={styles.inlineMessageBlock}>
                {msg.text || chatText('(sin texto)', '(no text)')}
              </div>

              <div style={styles.inlineMetaRow}>
                <span style={styles.messageTime}>{timeText}</span>
                <div style={styles.likeInline}>
                  <button
                    onClick={() => handleToggleLike(msg.id)}
                    style={styles.likeButton}
                    aria-label="Dar/Quitar like"
                    title="Me gusta"
                  >
                    {likesCount > 0 ? '❤️' : '🤍'}
                  </button>
                  <span style={styles.likeCount}>{likesCount}</span>
                </div>
              </div>
            </div>
          );
        })}
        <div ref={chatEndRef} />
      </div>

      {/* Composer flotante */}
      <div style={styles.composerWrap}>
        <button
          type="button"
          onClick={() => setShowEmojiPicker(prev => !prev)}
          style={styles.emojiToggle}
          aria-label="Mostrar/ocultar selector de emojis"
          title="Emojis"
        >
          😄
        </button>
        {showEmojiPicker && (
          <div ref={emojiPickerRef} style={styles.emojiPanelFloating}>
            <EmojiPicker
              onEmojiClick={addEmoji}
              locale="es"
              lazyLoadEmojis={true}
              searchDisabled={true}
              emojiStyle="native"
              theme="light"
            />
          </div>
        )}

        <form onSubmit={handleSendMessage} style={styles.form}>
          <input
            id="chatMessage"
            name="chatMessage"
            ref={inputRef}
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={chatText('Escribí tu mensaje…', 'Write your message…')}
            autoComplete="off"
            maxLength={MAX_MESSAGE_CHARS}
            aria-describedby="charCounter"
            style={styles.input}
          />
          <span id="charCounter" style={styles.charCounter}>
            {message.length}/{MAX_MESSAGE_CHARS}
          </span>
          <button type="submit" style={styles.sendButton} aria-label={chatText('Enviar mensaje', 'Send message')} title={chatText('Enviar', 'Send')}>
            💬
          </button>
        </form>
      </div>

      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 0,
    backgroundColor: 'transparent',
    isolation: 'isolate',
    color: '#fff',
    minHeight: 'auto',
    position: 'relative',
    fontFamily: "'Poppins', sans-serif",
    paddingBottom: 'calc(96px + env(safe-area-inset-bottom, 0px))',
    WebkitTapHighlightColor: 'transparent',
    zIndex: 3,
  },
  headerBar: {
    position: 'sticky',
    top: 0,
    zIndex: 10,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
    padding: '10px 12px',
    marginBottom: 12,
    background: 'linear-gradient(135deg, rgba(218,165,32,0.22), rgba(75,0,130,0.22))',
    border: '1px solid rgba(218,165,32,0.35)',
    borderRadius: 14,
    backdropFilter: 'blur(6px) saturate(1.02)',
    WebkitBackdropFilter: 'blur(6px) saturate(1.02)',
    boxShadow: '0 10px 24px rgba(0,0,0,0.25), inset 0 0 0 1px rgba(255,255,255,0.06)'
  },
  backButton: {
    backgroundImage: 'linear-gradient(135deg, #FFD670, #DAA520)',
    color: '#4B0082',
    border: 'none',
    borderRadius: 12,
    padding: '9px 13px',
    fontWeight: 900,
    cursor: 'pointer',
    boxShadow: '0 10px 22px rgba(218,165,32,0.38), 0 4px 10px rgba(0,0,0,0.22)',
    letterSpacing: 0.2,
    textShadow: '0 1px 0 rgba(255,255,255,0.5)'
  },

  usersFab: {
    position: 'absolute',
    top: 10,
    right: 10,
    zIndex: 20,
    background: 'linear-gradient(135deg, rgba(138,43,226,.88), rgba(75,0,130,.84))',
    color: '#FFE8A3',
    border: '1px solid rgba(218,165,32,.42)',
    borderRadius: 999,
    padding: '7px 11px',
    fontWeight: 950,
    fontSize: '.78rem',
    cursor: 'pointer',
    boxShadow: '0 10px 22px rgba(0,0,0,.30), 0 0 14px rgba(183,44,255,.18)',
    textShadow: '0 1px 2px rgba(0,0,0,.42)',
    backdropFilter: 'blur(10px) saturate(1.12)',
    WebkitBackdropFilter: 'blur(10px) saturate(1.12)',
  },
  usersModalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(5,0,14,0.62)',
    zIndex: 1100,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '48px 12px 12px',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
  },
  usersModal: {
    width: 'min(720px, 96vw)',
    maxHeight: '72dvh',
    background: 'linear-gradient(145deg, rgba(7,0,19,.94), rgba(31,5,68,.88))',
    border: '1px solid rgba(218,165,32,.45)',
    borderRadius: 22,
    boxShadow: '0 24px 62px rgba(0,0,0,.58), inset 0 1px 0 rgba(255,255,255,.08)',
    overflow: 'hidden',
  },
  usersModalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    padding: '13px 14px',
    borderBottom: '1px solid rgba(218,165,32,.30)',
    background: 'linear-gradient(135deg, rgba(138,43,226,.24), rgba(218,165,32,.10))',
  },
  usersModalTitle: {
    margin: 0,
    color: '#F3C96B',
    fontWeight: 950,
    letterSpacing: '.12em',
    textTransform: 'uppercase',
    fontSize: '.92rem',
  },
  usersModalClose: {
    background: 'rgba(255,255,255,.09)',
    border: '1px solid rgba(255,255,255,.14)',
    color: '#FFE8A3',
    borderRadius: 12,
    width: 34,
    height: 34,
    cursor: 'pointer',
    fontWeight: 950,
    lineHeight: 1,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 8px 18px rgba(0,0,0,.25)',
  },
  usersModalList: {
    padding: 12,
    overflowY: 'auto',
    maxHeight: 'calc(72dvh - 60px)',
    position: 'relative',
    overscrollBehavior: 'contain',
    scrollbarGutter: 'stable',
  },
  usersModalItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 10px',
    height: 60,
    borderRadius: 16,
    border: '1px solid rgba(218,165,32,.26)',
    background: 'linear-gradient(135deg, rgba(255,255,255,.08), rgba(255,255,255,.035))',
    marginBottom: 8,
    cursor: 'pointer',
    boxShadow: '0 8px 18px rgba(0,0,0,.22)',
    boxSizing: 'border-box',
  },
  usersModalAvatar: {
    width: 38,
    height: 38,
    borderRadius: '50%',
    objectFit: 'cover',
    border: '2px solid rgba(218,165,32,.82)',
    boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
    flexShrink: 0,
  },
  usersModalName: {
    fontWeight: 900,
    color: '#FFE8A3',
    textShadow: '0 1px 3px rgba(0,0,0,.45)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
  },
  usersModalSign: {
    fontSize: '1.05rem',
    flexShrink: 0,
  },
  usersModalGender: {
    fontSize: '1.05rem',
    flexShrink: 0,
  },

  chatBox: {
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid rgba(218,165,32,.38)',
    padding: 12,
    paddingTop: 52,
    flex: '1 1 auto',
    minHeight: 360,
    height: 'calc(100dvh - 190px)',
    overflowY: 'auto',
    marginBottom: 10,
    position: 'relative',
    background: 'linear-gradient(145deg, rgba(7,0,19,.82), rgba(31,5,68,.62))',
    backdropFilter: 'blur(18px) saturate(1.12)',
    WebkitBackdropFilter: 'blur(18px) saturate(1.12)',
    color: '#fff',
    borderRadius: 24,
    boxShadow: '0 24px 54px rgba(0,0,0,.34), inset 0 1px 0 rgba(255,255,255,.08)',
    scrollBehavior: 'auto',
    WebkitOverflowScrolling: 'touch',
    overflowAnchor: 'none',
    overscrollBehaviorY: 'contain',
    scrollbarGutter: 'stable both-edges',
    touchAction: 'pan-y',
    willChange: 'scroll-position',
    contain: 'layout paint style',
  },
 chatWheelLayer: {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none',
  overflow: 'hidden',
  zIndex: 0,
},

chatWheel: {
  position: 'absolute',
  top: '50%',
  left: '50%',
  width: 'min(120vw, 620px)',
  height: 'min(120vw, 620px)',
  transform: 'translate(-50%, -50%)',
  borderRadius: '50%',
  backgroundImage: 'url("/logo/Zodiacus.jpeg")',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
  backgroundSize: 'contain',
  opacity: 0.11,
  filter: 'drop-shadow(0 0 42px rgba(218,165,32,.32)) saturate(1.15)',
  animation: 'spin360 180s linear infinite',
  willChange: 'transform',
},
  systemMsg: {
    alignSelf: 'center',
    marginBottom: 10,
    fontStyle: 'normal',
    color: '#FFE8A3',
    background: 'rgba(255,255,255,.08)',
    border: '1px solid rgba(218,165,32,.28)',
    borderRadius: 999,
    padding: '7px 12px',
    fontWeight: 850,
    fontSize: '.78rem',
    boxShadow: '0 8px 18px rgba(0,0,0,.25)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    position: 'relative',
    zIndex: 1,
  },

  nameBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 10px',
    borderRadius: 999,
    background: 'linear-gradient(135deg, rgba(255,248,217,0.95), rgba(255,243,200,0.92))',
    border: '1px solid rgba(218,165,32,0.45)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    color: '#1f1147',
    marginBottom: 6,
    width: 'fit-content',
  },
  messageUserName: {
  fontWeight: 900,
  color: 'inherit',
  fontFamily: '"Cinzel", "Cormorant Garamond", serif',
  letterSpacing: '0.35px',
  textShadow: '0 1px 2px rgba(0,0,0,0.22)',
},

  messageTime: {
    fontSize: '0.7rem',
    color: 'rgba(255,255,255,.60)',
    fontStyle: 'normal',
    marginTop: 4,
    fontWeight: 700,
  },

  likeContainer: { display: 'flex', alignItems: 'center', marginLeft: 12, gap: 6 },
  likeButton: { fontSize: '1.4rem', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1 },
  likeCount: {
    fontSize: '0.85rem',
    color: '#FFE8A3',
    fontWeight: 850,
    userSelect: 'none',
  },

  composerWrap: {
    position: 'fixed',
    bottom: 'calc(env(safe-area-inset-bottom, 0px) + var(--kb-safe-offset, 0px) + var(--kb-base-gap, 18px))',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    gap: 9,
    padding: '9px 10px',
    boxSizing: 'border-box',
    width: 'min(720px, 92vw)',
    background: 'linear-gradient(145deg, rgba(7,0,19,.94), rgba(31,5,68,.88))',
    border: '1px solid rgba(218,165,32,.40)',
    borderRadius: 22,
    boxShadow: '0 18px 42px rgba(0,0,0,.46), inset 0 1px 0 rgba(255,255,255,.08)',
    backdropFilter: 'blur(18px) saturate(1.12)',
    WebkitBackdropFilter: 'blur(18px) saturate(1.12)',
    pointerEvents: 'auto',
  },
  form: {
    position: 'relative',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  input: {
    flex: 1,
    minWidth: 0,
    padding: '11px 12px',
    paddingRight: 88,
    fontSize: '0.98rem',
    borderRadius: 999,
    border: '1px solid rgba(218,165,32,.46)',
    outline: 'none',
    background: 'rgba(255,255,255,.10)',
    color: '#fff',
    fontWeight: 750,
    boxShadow: 'inset 0 2px 10px rgba(0,0,0,.22)',
    transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
  },
  sendButton: {
    width: 42,
    height: 42,
    minWidth: 42,
    padding: 0,
    background: 'linear-gradient(135deg,#8A2BE2 0%, #6F1CCB 48%, #DAA520 100%)',
    color: '#fff',
    border: '1px solid rgba(255,215,0,.45)',
    borderRadius: 999,
    fontWeight: 950,
    cursor: 'pointer',
    boxShadow: '0 12px 24px rgba(0,0,0,.32), 0 0 16px rgba(218,165,32,.20)',
    transition: 'transform 0.15s ease, box-shadow 0.2s ease',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
  },
  emojiToggle: {
    width: 42,
    height: 42,
    fontSize: '1.25rem',
    background: 'rgba(255,255,255,.10)',
    border: '1px solid rgba(218,165,32,.36)',
    color: '#FFE8A3',
    borderRadius: 14,
    cursor: 'pointer',
    padding: 0,
    boxShadow: '0 10px 22px rgba(0,0,0,.26)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emojiPanel: {
    position: 'absolute',
    zIndex: 100,
    backgroundColor: 'rgba(255,255,255,0.96)',
    borderRadius: 12,
    backdropFilter: 'blur(6px)',
    padding: 8,
    boxShadow: '0 10px 24px rgba(0,0,0,0.25)'
  },
  emojiPanelFloating: {
    position: 'absolute',
    bottom: '60px',
    left: '10px',
    zIndex: 1000,
    background: 'linear-gradient(145deg, rgba(255,255,255,.96), rgba(246,239,255,.94))',
    borderRadius: 12,
    backdropFilter: 'blur(6px)',
    padding: 8,
    boxShadow: '0 10px 24px rgba(0,0,0,0.25)'
  },
  // --- INLINE MESSAGE LAYOUT STYLES ---
  messageRowInlineBase: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    padding: '6px 8px',
    marginBottom: 6,
    borderRadius: 10,
    border: '1px solid rgba(0,0,0,0.06)',
    background: 'rgba(255,255,255,0.66)',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  },
  messageRowInlineMine: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    padding: '9px 10px',
    marginBottom: 9,
    borderRadius: 18,
    border: '1px solid rgba(183,44,255,.42)',
    background: 'linear-gradient(135deg, rgba(138,43,226,.28), rgba(75,0,130,.18))',
    boxShadow: '0 10px 22px rgba(0,0,0,.24)',
    position: 'relative',
    zIndex: 1,
  },

  messageRowInlineOther: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    padding: '9px 10px',
    marginBottom: 9,
    borderRadius: 18,
    border: '1px solid rgba(218,165,32,.34)',
    background: 'linear-gradient(135deg, rgba(218,165,32,.18), rgba(255,255,255,.055))',
    boxShadow: '0 10px 22px rgba(0,0,0,.22)',
    position: 'relative',
    zIndex: 1,
  },

  inlineTopRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  inlineAvatar: {
    width: 38,
    height: 38,
    borderRadius: '50%',
    objectFit: 'cover',
    flexShrink: 0,
    marginRight: 8,
    border: '2px solid transparent',
    boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
    cursor: 'pointer',
    transform: 'translateY(2px)',
  },
  inlineAvatarMine: {
    borderColor: '#7C3AED', /* violeta */
  },
  inlineAvatarOther: {
    borderColor: '#DAA520', /* dorado */
  },
  userNameMine: {
    fontWeight: 950,
    color: '#FFE8A3',
    letterSpacing: '0.2px',
    fontSize: '0.94rem',
    cursor: 'pointer',
    textShadow: '0 1px 3px rgba(0,0,0,.40)',
  },
  userNameOther: {
    fontWeight: 950,
    color: '#FFFFFF',
    letterSpacing: '0.2px',
    fontSize: '0.94rem',
    cursor: 'pointer',
    textShadow: '0 1px 3px rgba(0,0,0,.40)',
  },
  inlineEmoji: {
    display: 'inline-block',
    minWidth: 18,
    textAlign: 'center',
    fontSize: '1rem',
    lineHeight: 1,
    opacity: 1,
  },
  inlineAddBtnTiny: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 24,
    padding: '0 9px',
    borderRadius: 999,
    background: 'rgba(255,255,255,.10)',
    border: '1px solid rgba(218,165,32,.28)',
    color: '#FFE8A3',
    fontWeight: 850,
    fontSize: '0.74rem',
    cursor: 'pointer',
    boxShadow: '0 6px 12px rgba(0,0,0,.20)',
    transform: 'translateY(1px)',
  },
  inlineMessageBlock: {
    marginLeft: 46,
    color: 'rgba(255,255,255,.92)',
    fontWeight: 600,
    fontSize: '0.94rem',
    lineHeight: 1.36,
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  },
  inlineMetaRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 10,
    paddingRight: 2,
    marginTop: 2,
    marginLeft: 46,
  },
  likeInline: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  },

  /* 👇 NUEVOS estilos para nombre y emojis en Usuarios en sala */
  userNameInline: {
    fontFamily: "'Poppins', sans-serif",
    fontWeight: 900,
    letterSpacing: '0.3px',
    fontSize: 'clamp(1rem, 3.4vw, 1.15rem)',
    lineHeight: 1.2,
    color: '#FFFFFF',
    textShadow: '0 1px 3px rgba(0,0,0,0.6), 0 0 8px rgba(255,215,0,0.5)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '52vw',
    marginRight: 8,
  },
  userChipSign: { fontSize: '1.15rem', lineHeight: 1, flexShrink: 0, marginRight: 6 },
  userChipGender: { display: 'inline-block', minWidth: 18, textAlign: 'center', fontSize: '1.15rem', lineHeight: 1, flexShrink: 0 },
  userAddBtn: {
    marginLeft: 'auto',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,214,101,0.9)',
    color: '#FFE28A',
    borderRadius: 999,
    padding: '2px 10px',
    fontSize: '0.82rem',
    fontWeight: 800,
    lineHeight: 1.2,
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
    textShadow: '0 1px 2px rgba(0,0,0,0.65)',
    backdropFilter: 'blur(2px)',
    WebkitBackdropFilter: 'blur(2px)',
    transition: 'transform 120ms ease, box-shadow 120ms ease',
  },
  inlineAddBtn: {
    marginLeft: 8,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#FFFFFF',
    border: '1px solid rgba(0,0,0,0.12)',
    color: '#4B0082',
    borderRadius: 10,
    padding: '0 8px',
    fontSize: '1rem',
    fontWeight: 800,
    lineHeight: 1.2,
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
    textShadow: 'none',
    backdropFilter: 'blur(2px)',
    WebkitBackdropFilter: 'blur(2px)',
    transition: 'transform 120ms ease, box-shadow 120ms ease',
  },
  charCounter: {
    position: 'absolute',
    right: 60,
    bottom: 11,
    fontSize: '0.70rem',
    color: 'rgba(255,255,255,.62)',
    fontWeight: 800,
    userSelect: 'none',
    background: 'rgba(255,255,255,.08)',
    border: '1px solid rgba(218,165,32,.24)',
    borderRadius: 8,
    padding: '2px 6px',
    lineHeight: 1,
    pointerEvents: 'none',
  },
};

export default ChatRoom;
